import type { AbsUnit, Block, BlockItem, BlockKind, ChainPart, Exercise, Line, Load } from './types';
import { findFmt, parseFmt, stripFmt } from './format';
import { REST_ID, fmtNum, mkBlock, mkChain, mkLine, mkSet, nid, normSets } from './content';
import { extractParams, norm, parseLoadField, parseSchemeField } from './parser';

/* Кнопка AI (CON-5) через LLM: модель возвращает JSON по AI_SCHEMA, aiToBlocks переводит его
   в блоки конструктора. Схему, нагрузку и формат комплекса модель пишет строками в нашей
   нотации («5×5», «70%», «AMRAP 12») — разбирают их те же функции, что и ручной ввод, так что
   правила одни. Упражнение берётся только по коду из базы; нет кода — строка остаётся текстом
   (лучше честный текст, чем правдоподобная подмена). */

export interface AiPart {
  ex: string;
  name: string;
  reps: string;
}
export interface AiLine {
  type: 'ex' | 'chain' | 'text' | 'rest';
  src: string;
  ex: string;
  /** Название упражнения из базы для кода ex — сверяется с кодом (модель путает соседние коды) */
  name: string;
  scheme: string;
  load: string;
  txt: string;
  sets: number;
  parts: AiPart[];
  /** Уточнение к строке, которое не ложится в упражнение и схему («под углом 35°», «строго по технике») — в заметку блока */
  note?: string;
}
export interface AiItem extends Omit<AiLine, 'type'> {
  type: AiLine['type'] | 'set';
  rounds: number;
  rest: string;
  items: AiLine[];
}
export interface AiBlock {
  title: string;
  kind: BlockKind | 'none';
  format: string;
  note: string;
  items: AiItem[];
}
export interface AiResult {
  blocks: AiBlock[];
}

const KINDS: (BlockKind | 'none')[] = ['none', 'warmup', 'strength', 'complex', 'cooldown', 'stretch', 'accessory', 'gymnastics'];

const str = (description: string) => ({ type: 'string', description });
const lineProps = {
  type: { type: 'string', enum: ['ex', 'chain', 'rest', 'text'] },
  src: str('Исходная строка тренера дословно'),
  ex: str('Код упражнения из базы (ex) или "" — если упражнения нет в базе или не уверен'),
  name: str('Есть код — название из базы слово в слово; нет кода — название упражнения, как написал тренер, без чисел, единиц и схемы'),
  scheme: str('Схема: «5×5», «4×8-10», «5-5-3-3-1», «10», «3×(1+1)» для связки; "" — нет'),
  load: str('Нагрузка с единицей: «70%», «70-80%», «40 кг», «500 м», «60 сек», «2 мин», «15 кал»; "" — нет'),
  txt: str('Сложная запись при узнанном упражнении («60×5, 70×5, 80×3×3», «до отказа», дроп-сет) — тогда scheme и load пустые; иначе ""'),
  sets: { type: 'integer', description: 'Только для связки: число подходов, иначе 0' },
  parts: {
    type: 'array',
    description: 'Только для связки: упражнения по порядку, иначе []',
    items: {
      type: 'object',
      additionalProperties: false,
      required: ['ex', 'name', 'reps'],
      properties: { ex: str('Код из базы или ""'), name: str('Название из базы для кода (слово в слово), а если кода нет — как написано'), reps: str('Повторы части или ""') },
    },
  },
  note: str('Уточнение к упражнению, которому нет места в названии из базы, схеме и нагрузке («под углом 35°», «высота 60 см», «пауза внизу»), иначе ""'),
};
const lineSchema = { type: 'object', additionalProperties: false, required: Object.keys(lineProps), properties: lineProps };
const itemProps = {
  ...lineProps,
  type: { type: 'string', enum: ['ex', 'set', 'chain', 'rest', 'text'] },
  rounds: { type: 'integer', description: 'Только для сета: число кругов, иначе 0' },
  rest: str('Только для сета: отдых между кругами («90 сек», «2 мин») или ""'),
  items: { type: 'array', description: 'Только для сета: упражнения сета, иначе []', items: lineSchema },
};

/** JSON Schema ответа модели (OpenAI structured outputs, strict) */
export const AI_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['blocks'],
  properties: {
    blocks: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['title', 'kind', 'format', 'note', 'items'],
        properties: {
          title: str('Название блока без формата; "" — если у блока нет заголовка'),
          kind: { type: 'string', enum: KINDS },
          format: str('Формат комплекса: «AMRAP 12», «EMOM 10», «E2MOM 12», «For time», «5 RFT», «Tabata», «Death by 1», «NFT», «8 раундов 30 сек работа 15 сек отдых»; "" — не комплекс'),
          note: str('Заметка к блоку для клиента (отдых, темп, указания) или ""'),
          items: { type: 'array', items: { type: 'object', additionalProperties: false, required: Object.keys(itemProps), properties: itemProps } },
        },
      },
    },
  },
} as const;

type Lookup = (code: string, name?: string) => Exercise | undefined;

const defUnit = (e: Exercise | undefined): AbsUnit => (e?.units.includes('кг') ? 'кг' : e?.units[0] || 'кг');

/** «5 км» → «5000 м»: дистанция в базе — в метрах */
const km = (v: string) => v.replace(/(\d+(?:[.,]\d+)?)\s*км[а-яё]*\.?/gi, (_, n) => String(Math.round(parseFloat(n.replace(',', '.')) * 1000)) + ' м');

function toLoad(raw: string, e?: Exercise): Load | null | 'text' {
  const r = parseLoadField(km(raw));
  if ('empty' in r) return null;
  if ('text' in r) return 'text';
  const unit = r.unit ?? defUnit(e);
  const rng = r.to != null ? { to: r.to } : {};
  return unit === '%' ? { kind: 'pct', from: r.from, ...rng } : { kind: 'abs', unit, from: r.from, ...rng };
}

const squash = (v: string) => v.toLowerCase().replace(/[x×хХ*]/g, 'x').replace(/\s+/g, '');

/** Повторы и нагрузка строки (упражнение из базы или названное текстом) */
function params(item: Line & { t: 'ex' }, x: AiLine, e: Exercise | undefined): Line {
  /* Модель иногда кладёт в txt только хвост («в лёгком темпе», «на каждую ногу»), а подходы и вес — в
     scheme/load: тогда они теряются. Всё, чего нет в txt, дописываем впереди */
  let txt = km(x.txt.trim());
  /* Модель иногда пишет в txt всю строку вместе с названием: «Подтягивания — 5 подходов — до отказа» */
  const head = (x.src || '').match(/^[^\d]*?[а-яёa-z][^\d]*?(?=\s*[-–—:,]?\s*\d)/i)?.[0].trim();
  if (head && head.length > 2 && txt.toLowerCase().startsWith(head.toLowerCase())) txt = txt.slice(head.length).replace(/^[\s:—–,-]+/, '');
  if (txt) {
    const has = (v: string) => squash(txt).includes(squash(v));
    item.txt = [x.scheme.trim(), x.load.trim()].filter((v) => v && !has(v)).concat(txt).join(' ');
    return item;
  }
  const sc = x.scheme.trim() ? parseSchemeField(x.scheme) : { scheme: '', load: null };
  const ld = x.load.trim() ? toLoad(x.load, e) : null;
  if ('txt' in sc || ld === 'text') {
    item.txt = [x.scheme, x.load].map((s) => s.trim()).filter(Boolean).join(' ');
    return item;
  }
  const load = ld || sc.load;
  let scheme = sc.scheme;
  /* Время, дистанция, калории — нагрузка, а не повторы: «12 кал» не даёт схему «12», «3-4×45/45» при 45 сек — это «3-4×» */
  if (scheme && load?.kind === 'abs' && load.unit !== 'кг' && load.unit !== 'повт') {
    const n = fmtNum(load.from);
    const m = scheme.match(/^(?:(\d+(?:-\d+)?)×)?([\d,./-]+)$/);
    if (m && m[2].split(/[/-]/).every((v) => v === n || v === String(load.from))) scheme = m[1] ? m[1] + '×' : '';
  }
  if (scheme) item.scheme = scheme;
  if (load) item.load = load;
  return item;
}

/** Строка, оставленная текстом, но с числом и единицей при названии («Растяжка 15 мин», «10 мин ходьба») — с параметрами */
function textWithParams(src: string): Line | null {
  if (!/\d/.test(src) || /[!?]/.test(src)) return null;
  const { scheme, load, rest } = extractParams(src);
  const name = rest.replace(/[\s:—–,.-]+$/, '').replace(/^[\s:—–,.-]+/, '').replace(/\s+/g, ' ').trim();
  /* Служебные строки — не упражнения: «Лимит 12 мин», «Кэп 15 мин», «Итого 40 мин» */
  if (!load || load.kind !== 'abs' || !/[а-яёa-z]{3}/i.test(name) || /\d/.test(name) || /^(отдых|лимит|кэп|тайм ?кэп|time ?cap|cap|итого|всего|время|на время)/i.test(name)) return null;
  const it = mkLine(null, name.charAt(0).toUpperCase() + name.slice(1));
  if (scheme) it.scheme = scheme;
  it.load = load;
  return it;
}

/** Строка-схема без упражнения: «21-15-9:», «- 21-15-9-6:» */
const schemeOnly = (src: string) => src.replace(/^[\s•*–—-]+/, '').replace(/[\s:.]+$/, '').match(/^\d+(?:\s*[-–/]\s*\d+)+$/)?.[0].replace(/\s|–/g, (c) => (c === '–' ? '-' : ''));

/** Отдых строкой: «1,5 мин», «90 сек» → нагрузка во времени; без времени — null */
/** Отдых сета строкой, как его вводит тренер: «90 сек», «1,5 мин» */
const restText = (l: Load) => fmtNum(l.from) + (l.to != null ? '-' + fmtNum(l.to) : '') + ' ' + (l.kind === 'pct' ? '%' : l.unit);

function restLoad(raw: string): Load | null {
  const l = toLoad(raw || '', undefined);
  if (!l || l === 'text' || l.kind !== 'abs') return null;
  if (l.unit === 'сек' || l.unit === 'мин') return l;
  return null;
}

function toLine(x: AiLine, find: Lookup): Line {
  const src = (x.src || '').trim();
  if (x.type === 'rest') {
    const load = restLoad(x.load) || restLoad(src.replace(/отдых[а-яё]*/gi, ''));
    if (!load) return mkLine(null, src);
    const it = mkLine(REST_ID);
    it.load = load;
    return it;
  }
  if (x.type === 'chain' && x.parts.length >= 2) {
    const parts: ChainPart[] = x.parts.map((p) => {
      const e = p.ex ? find(p.ex, p.name) : undefined;
      return { id: nid(), exId: e ? e.id : null, ...(e ? {} : { raw: p.name.trim() }), ...(p.reps.trim() ? { reps: p.reps.trim() } : {}) };
    });
    const c = mkChain(parts);
    if (x.sets > 0) c.sets = x.sets;
    const load = x.load.trim() ? toLoad(x.load, undefined) : null;
    if (load && load !== 'text') c.load = load;
    return c;
  }
  /* Модель иногда теряет число («20 ОВСНР» → без повторов): числа строки не пропадают — разбираем их сами */
  if (x.type === 'ex' && !x.scheme.trim() && !x.load.trim() && !x.txt.trim() && /\d/.test(src)) {
    const p = extractParams(src.split('\n').join(' '));
    const ld = p.load ? (p.load.kind === 'pct' ? fmtNum(p.load.from) + (p.load.to != null ? '-' + fmtNum(p.load.to) : '') + '%' : restText(p.load)) : '';
    x = { ...x, scheme: p.scheme, load: ld };
  }
  const e = x.type === 'ex' && x.ex ? find(x.ex, x.name) : undefined;
  if (e) return params(mkLine(e.id), x, e);
  /* Упражнения нет в базе: название — текстом (тренер потом выберет из базы), но повторы и вес — параметрами строки */
  const name = x.type === 'ex' ? x.name.trim() : '';
  if (name && (x.scheme.trim() || x.load.trim() || x.txt.trim())) return params(mkLine(null, name), x, undefined);
  return (x.type === 'text' && textWithParams(src)) || mkLine(null, src || [x.scheme, x.load].filter(Boolean).join(' '));
}

/* Остаток заголовка «Амрап 12 мин» после вырезания формата — тоже формат, не название */
const FMT_WORD = /^(амрап|amrap|емом|эмом|emom|на время|фор тайм|for time|табата|tabata|комплекс|wod|вод)[\s:.,-]*(\d+\s*(мин[а-яё]*)?)?$/i;

/* Слова формата комплекса в тексте тренера: без них формат — выдумка модели («Комплекс» ≠ «на время») */
const FMT_IN_TEXT = /amrap|амрап|emom|e\dmom|емом|эмом|на время|фор ?тайм|for ?time|rft|табат|tabata|death by|nft|кажд[а-яё]* (\d+ )?мин|раунд[а-яё]* на время|максимум раундов|работа/i;

/** Ответ модели → блоки конструктора. find — упражнение по коду, который видела модель; text — исходный текст тренера. */
export function aiToBlocks(res: AiResult, find: Lookup, text?: string): Block[] {
  const noFmt = text != null && !FMT_IN_TEXT.test(text.replace(/не\s+на\s+время/gi, ' '));
  return (res.blocks || [])
    .map((x) => {
      /* Уточнения строк, которым нет места в строке, — в заметку блока: «Жим гантелей на наклонной скамье — под углом 35°» */
      const notes: string[] = [];
      const noteOf = (z: AiLine) => {
        const n = (z.note || '').trim();
        const all = [x.note, ...notes].join('\n').toLowerCase();
        if (n && !all.includes(n.toLowerCase())) notes.push((z.name.trim() ? z.name.trim() + ' — ' : '') + n);
      };
      /* Строка-схема перед списком («- 21-15-9-6:») — схема упражнений блока, а не строка */
      const head = x.items.map((y) => (y.type === 'text' ? schemeOnly(y.src) : undefined)).find(Boolean);
      if (head) {
        x.items = x.items.filter((y) => !(y.type === 'text' && schemeOnly(y.src)));
        for (const y of x.items) for (const z of y.type === 'set' ? y.items : [y]) if (z.type === 'ex' && !z.scheme.trim() && !z.txt.trim()) z.scheme = head;
      }
      const items: BlockItem[] = x.items.map((y) => {
        if (y.type !== 'set') {
          noteOf(y as AiLine);
          return toLine({ ...y, type: y.type }, find);
        }
        y.items.forEach(noteOf);
        /* Отдых строкой внутри сета — это отдых между кругами */
        let rest = y.rest.trim();
        const lines = y.items.filter((z) => {
          if (z.type !== 'rest') return true;
          const l = restLoad(z.load) || restLoad(z.src.replace(/отдых[а-яё]*/gi, ''));
          if (!l || rest) return !l;
          rest = restText(l);
          return false;
        });
        const rounds = y.rounds > 0 ? y.rounds : 3;
        const set = mkSet(lines.map((z) => toLine(z, find)), rounds, restLoad(rest) ? restText(restLoad(rest)!) : rest);
        /* Подходы в сете — это его круги: «А2. Тяга 3×10» в сете на 3 круга → «10» */
        for (const l of set.items) if (l.t === 'ex' && l.scheme && l.scheme.startsWith(rounds + '×') && l.scheme.length > String(rounds).length + 1) l.scheme = l.scheme.slice(String(rounds).length + 1);
        return set;
      });
      /* Модель иногда оставляет формат и в названии («EMOM 12» + format «EMOM 12») — вырезаем */
      const f = noFmt ? null : parseFmt(x.format) || findFmt(x.title);
      let title = f ? stripFmt(x.title).trim() : x.title.trim();
      if (f && FMT_WORD.test(title)) title = '';
      /* Тип блока ставим только комплексу (формат AMRAP, EMOM, на время…): разминку, силовую и прочее модель
         угадывает ненадёжно — тренер выберет сам */
      const note = [x.note.trim(), ...notes].filter(Boolean).join('\n');
      const b = mkBlock({ title, kind: x.kind === 'complex' ? 'complex' : null, note, items });
      if (f) {
        const { src: _src, ...fmt } = f;
        b.fmt = fmt;
        b.kind = 'complex';
      } else if (x.format.trim() && !b.title && !noFmt) b.title = x.format.trim();
      return normSets(b);
    })
    .filter((b) => b.items.length)
    .map((b) => {
      /* «Отдых» сразу после сета без своего отдыха — это отдых между его кругами */
      b.items = b.items.filter((y, k) => {
        const prev = b.items[k - 1];
        if (!(y.t === 'ex' && y.exId === REST_ID && y.load?.kind === 'abs' && prev?.t === 'set' && !prev.rest)) return true;
        prev.rest = restText(y.load);
        return false;
      });
      return b;
    })
    /* Пустая строка блок не делит: блок без заголовка и формата — продолжение предыдущего (тоже не комплекса) */
    .reduce<Block[]>((out, b) => {
      const prev = out[out.length - 1];
      if (prev && !b.title && !b.fmt && b.kind !== 'complex' && !prev.fmt && prev.kind !== 'complex') {
        prev.items.push(...b.items);
        prev.note = [prev.note, b.note].filter(Boolean).join('\n');
      } else out.push(b);
      return out;
    }, []);
}

/** Код упражнения для промпта: общая база — свой id (стабилен → кэш промпта), личная — по порядку. */
const words = (s: string) => new Set(norm(s).split(' ').filter((w) => w.length > 1));
/** Название, которое написала модель, — про это упражнение: совпадает, входит одно в другое или ≥60% общих слов */
export function sameName(a: string, b: string): boolean {
  const x = norm(a),
    y = norm(b);
  if (!x || !y) return false;
  if (x === y || x.includes(y) || y.includes(x)) return true;
  const A = words(a),
    B = words(b);
  let hit = 0;
  for (const w of A) if ([...B].some((v) => v === w || (w.length >= 5 && v.length >= 5 && w.slice(0, 5) === v.slice(0, 5)))) hit++;
  return hit / Math.max(A.size, B.size) >= 0.6;
}

/** Упражнение по коду из ответа модели — только если название сходится; иначе ищем по точному названию, иначе ничего */
export function aiResolver(codes: Map<string, Exercise>, exs: Exercise[]) {
  const byName = new Map(exs.map((e) => [norm(e.ru), e]));
  return (code: string, name?: string): Exercise | undefined => {
    const e = codes.get(code);
    if (!name || !name.trim()) return e;
    if (e && sameName(e.ru, name)) return e;
    return byName.get(norm(name));
  };
}

export function aiCodes(exs: Exercise[]): { codes: Map<string, Exercise>; common: string; own: string } {
  const codes = new Map<string, Exercise>();
  const line = (code: string, e: Exercise) => {
    codes.set(code, e);
    const al = (e.aliases || []).filter((a) => a && a.toLowerCase() !== e.ru.toLowerCase()).slice(0, 10);
    return `${code} ${e.ru}${al.length ? ' (' + al.join(', ') + ')' : ''}`;
  };
  const common = exs
    .filter((e) => !e.own)
    .sort((a, b) => (a.id < b.id ? -1 : 1))
    .map((e) => line(e.id, e))
    .join('\n');
  const own = exs
    .filter((e) => e.own)
    .map((e, i) => line('own' + (i + 1), e))
    .join('\n');
  return { codes, common, own };
}

/** Сводка разбора для журнала: блоков, строк, сколько из базы и сколько осталось текстом */
export function aiStat(blocks: Block[]): { blocks: number; lines: number; linked: number; text: number } {
  const lines: Line[] = blocks.flatMap((b) => b.items.flatMap((x) => (x.t === 'set' ? x.items : [x])));
  const linked = lines.filter((l) => (l.t === 'chain' ? l.parts.some((p) => p.exId) : !!l.exId)).length;
  return { blocks: blocks.length, lines: lines.length, linked, text: lines.length - linked };
}
